<script setup lang="ts">
import { useSnackbarStore } from '@/store/snackbar'
import { $multipleUpdateLineItemExtRefNum } from '@/api/brandings'
import { $multipleUpdateSignExtRefNum } from '@/api/signs'
import { $multipleUpdateKopItemExtRefNum } from '@/api/kop'

const snackbarStore = useSnackbarStore()

interface IProps {
  modelValue?: boolean,
  campaignType?: 'wayfinding' | 'branding' | 'kop',
  selectedItemIds?: number[],
  baseExtRefNum?: string,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  campaignType: 'wayfinding',
  selectedItemIds: () => ([]),
  baseExtRefNum: '',
})

const emit = defineEmits(['update:modelValue', 'onSubmit', 'onClose'])

const currentExtRefNum = ref<string>('')
const dialogLoading = ref<boolean>(false)

const onSubmit = async () => {
  // KOP
  if (props.campaignType === 'kop') {
    await multipleExtRefNumUpdateKopItems()
  }
  // Wayfinding
  else if (props.campaignType === 'wayfinding') {
    await multipleExtRefNumUpdateSigns()
  }
  // Branding
  else {
    await multipleExtRefNumUpdateLineItems()
  }

  emit('onSubmit')
  closeDialog()
}

const multipleExtRefNumUpdateKopItems = async () => {
  dialogLoading.value = true
  const payload = {
    ext_ref_num: currentExtRefNum.value,
    kop_item_ids: props.selectedItemIds
  }
  try {
    await $multipleUpdateKopItemExtRefNum(payload)
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'KOP Item(s) Ext Ref # updated successfully!'
    })
  } catch (e: any) {
    const mes = e.response.data ? JSON.stringify(e.response.data) : e.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    dialogLoading.value = false
  }
}

const multipleExtRefNumUpdateSigns = async () => {
  dialogLoading.value = true
  const payload = {
    ext_ref_num: currentExtRefNum.value,
    sign_ids: props.selectedItemIds
  }
  try {
    await $multipleUpdateSignExtRefNum(payload)
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Sign(s) Ext Ref # updated successfully!'
    })
  } catch (e: any) {
    const mes = e.response.data ? JSON.stringify(e.response.data) : e.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    dialogLoading.value = false
  }
}

const multipleExtRefNumUpdateLineItems = async () => {
  dialogLoading.value = true
  const payload = {
    ext_ref_num: currentExtRefNum.value,
    line_item_ids: props.selectedItemIds
  }
  try {
    await $multipleUpdateLineItemExtRefNum(payload)
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Item(s) Ext Ref # updated successfully!'
    })
  } catch (e: any) {
    const mes = e.response.data ? JSON.stringify(e.response.data) : e.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    dialogLoading.value = false
  }
}

const closeDialog = () => {
  emit('update:modelValue', !props.modelValue)
}

const onClose = async () => {
  currentExtRefNum.value = ''
  emit('onClose')
}

watch(() => props.modelValue, () => {
  // on show Dialog
  if (!!props.modelValue) {
    currentExtRefNum.value = props.baseExtRefNum
  }
  // on hide Dialog
  else {
    onClose()
  }
})
</script>

<template>
  <div>
    <VDialog
      :model-value="modelValue"
      max-width="600"
      class="default-dialog"
      :persistent="!!dialogLoading"
      @after-leave="onClose"
      @update:modelValue="emit('update:modelValue')"
    >
      <VBtn
        variant="elevated"
        density="compact"
        icon="tabler-x"
        class="v-dialog-close-btn"
        :disabled="!!dialogLoading"
        @click="closeDialog"
      />

      <VCard title="Update Ext Ref #">
        <VCardText>
          <VRow>
            <VCol class="py-0">
              <VTextarea
                v-model="currentExtRefNum"
                label="Ext Ref #"
                autofocus
                rows="3"
              />
            </VCol>
          </VRow>
          <VRow>
            <VCol
              class="d-flex justify-end"
              cols="12"
            >
              <VBtn
                :disabled="!!dialogLoading"
                @click="onSubmit"
              >
                <VProgressCircular
                  v-if="dialogLoading"
                  class="mr-1"
                  size="x-small"
                  :width="2"
                  indeterminate
                  color="light"
                />
                Update
              </VBtn>
            </VCol>
          </VRow>
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>
